import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AuthResponse } from '../services/auth/models/auth.model';
import { User } from './models/user.model';
import { Account } from '../interfaces/account.interface';
import { ModuleType } from '../interfaces/module.interface';
import {
  CameraPresetsBundle,
  CameraPresetsStorageV1,
  CustomCameraPreset,
  CustomCameraPresetsStorageV1,
} from './models/camera-presets.model';
import { normalizePrivilegeMap } from '../services/authorization/privilege.utils';
import { AuthTokenService } from '../services/auth/auth-token.service';

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private readonly USER_KEY = 'currentUser';
  private readonly ACCOUNT_KEY = 'currentAccount';
  private readonly ACCOUNT_SELECTED_KEY = 'accountSelected';
  private readonly MODULES_KEY = 'availableModules';
  private readonly CURRENT_MODULE_KEY = 'currentModule';
  private readonly CAMERA_ID = 'cameraId'

  private readonly CAMERA_IDS = 'cameraIds';

  private readonly CAMERA_PRESETS = 'cameraPresets:v1';
  private readonly CUSTOM_CAMERA_PRESETS = 'cameraCustomPresets:v1';
  private readonly LEGACY_SESSION_TOKEN_KEY = 'authToken';
  private readonly LEGACY_CAMERA_TOKEN_KEY = 'tokenCameraIot';

  private cameraToken: string | null = null;
  private multiCameraToken: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private readonly authTokenService: AuthTokenService,
  ) {
    this.migrateLegacyTokens();
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private getItem(key: string): string | null {
    return this.isBrowser ? window.localStorage.getItem(key) : null;
  }

  private setItem(key: string, value: string): void {
    if (this.isBrowser) {
      window.localStorage.setItem(key, value);
    }
  }

  private removeItem(key: string): void {
    if (this.isBrowser) {
      window.localStorage.removeItem(key);
    }
  }

  private migrateLegacyTokens(): void {
    if (!this.isBrowser) return;

    const storedUser = this.getItem(this.USER_KEY);
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser) as User & { token?: unknown };
        if (typeof parsedUser.token === 'string' && parsedUser.token.trim()) {
          this.authTokenService.setToken(parsedUser.token);
        }

        if (Object.prototype.hasOwnProperty.call(parsedUser, 'token')) {
          delete parsedUser.token;
          this.setItem(this.USER_KEY, JSON.stringify(parsedUser));
        }
      } catch {
        this.removeItem(this.USER_KEY);
      }
    }

    // Remove credentials written by previous versions. Camera IDs are not
    // credentials and remain available for restoring the UI state.
    this.removeItem(this.LEGACY_SESSION_TOKEN_KEY);
    this.removeItem(this.LEGACY_CAMERA_TOKEN_KEY);
  }

  private getCameraPresetsKey(cameraId?: number): string {
    const userId = this.getCurrentUser()?.id_user ?? 0;
    const accountId = this.getAccountSelected()?.id ?? 0;

    // Default storage does not depend on cameraId, following the "user session" mold:
    // presets are scoped by account + user and reused across cameras.
    if (cameraId === undefined || cameraId === null) {
      return `${this.CAMERA_PRESETS}:account:${accountId}:user:${userId}`;
    }

    return `${this.CAMERA_PRESETS}:account:${accountId}:user:${userId}:camera:${cameraId}`;
  }

  private getCustomCameraPresetsKey(): string {
    const userId = this.getCurrentUser()?.id_user ?? 0;
    const accountId = this.getAccountSelected()?.id ?? 0;
    return `${this.CUSTOM_CAMERA_PRESETS}:account:${accountId}:user:${userId}`;
  }

  private isCameraPresetsStorageV1(value: any): value is CameraPresetsStorageV1 {
    if (!value || typeof value !== 'object') return false;
    if (value.version !== 1) return false;
    if (typeof value.updatedAt !== 'string') return false;

    const hasPreset = (p: any) =>
      p &&
      typeof p === 'object' &&
      typeof p.pan === 'number' &&
      typeof p.tilt === 'number' &&
      typeof p.zoom === 'number';

    return hasPreset(value.preset1) && hasPreset(value.preset2);
  }

  getCameraPresets(cameraId?: number): CameraPresetsStorageV1 | null {
    const raw = this.getItem(this.getCameraPresetsKey(cameraId));
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return this.isCameraPresetsStorageV1(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  saveCameraPresets(bundle: CameraPresetsBundle, cameraId?: number): void {
    const payload: CameraPresetsStorageV1 = {
      version: 1,
      updatedAt: new Date().toISOString(),
      preset1: bundle.preset1,
      preset2: bundle.preset2,
    };

    this.setItem(this.getCameraPresetsKey(cameraId), JSON.stringify(payload));
  }

  removeCameraPresets(cameraId?: number): void {
    this.removeItem(this.getCameraPresetsKey(cameraId));
  }

  getCustomCameraPresets(): CustomCameraPreset[] {
    const raw = this.getItem(this.getCustomCameraPresetsKey());
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw) as Partial<CustomCameraPresetsStorageV1>;
      if (parsed.version !== 1 || !Array.isArray(parsed.presets)) return [];

      return parsed.presets
        .filter((preset): preset is CustomCameraPreset =>
          !!preset &&
          [1, 2, 3].includes(preset.slot) &&
          typeof preset.name === 'string' &&
          typeof preset.pan === 'number' && Number.isFinite(preset.pan) &&
          typeof preset.tilt === 'number' && Number.isFinite(preset.tilt) &&
          typeof preset.zoom === 'number' && Number.isFinite(preset.zoom)
        )
        .slice(0, 3)
        .sort((a, b) => a.slot - b.slot);
    } catch {
      return [];
    }
  }

  saveCustomCameraPresets(presets: CustomCameraPreset[]): void {
    const payload: CustomCameraPresetsStorageV1 = {
      version: 1,
      updatedAt: new Date().toISOString(),
      presets: presets
        .filter(preset => [1, 2, 3].includes(preset.slot))
        .slice(0, 3)
        .sort((a, b) => a.slot - b.slot),
    };

    this.setItem(this.getCustomCameraPresetsKey(), JSON.stringify(payload));
  }

  saveDatalogin(response: AuthResponse): void {
    this.authTokenService.setToken(response.data.token);

    const user: User = {
      username: response.data.username,
      id_user: response.data.id_user,
      id_person: response.data.id_person,
      role_name: response.data.acl?.role_name || '',
      is_admin_account: response.data.acl?.is_admin_account || false,
      is_admin: response.data.acl?.is_admin || false,
      is_coordinator: response.data.acl?.is_coordinator || false,
      has_account_access: response.data.acl?.has_account_access || false,
      privileges: normalizePrivilegeMap(response.data.acl?.privileges),
    };

    const account: Account = {
      id: response.data.acl?.id_account || 0,
      name: response.data.account_name,
      alias: response.data.alias,
      created_at: "Data Não Disponível",
      is_admin: response.data.acl?.is_admin_account || false,
      is_coordinator: response.data.acl?.is_coordinator || false,
      billing: false,
      state: response.data.state || '',
      city: response.data.city || '',
      url_account: "https://www.example.com",
    };

    this.setItem(this.USER_KEY, JSON.stringify(user));
    this.setItem(this.ACCOUNT_KEY, JSON.stringify(account));
    this.setItem(this.ACCOUNT_SELECTED_KEY, JSON.stringify(account));

    //console.log('response', response.data.module)
    //const availableModules = [ModuleType.PublicIllumination.id];
    this.saveAvailableModules(response.data.module);
    //this.setCurrentModule(availableModules[0]);
  }

  logout(): void {
    this.authTokenService.clear();
    this.cameraToken = null;
    this.multiCameraToken = null;

    this.removeItem(this.USER_KEY);
    this.removeItem(this.ACCOUNT_KEY);
    this.removeItem(this.ACCOUNT_SELECTED_KEY);
    this.removeItem(this.MODULES_KEY);
    this.removeItem(this.CURRENT_MODULE_KEY);
    this.removeItem(this.LEGACY_SESSION_TOKEN_KEY);
    this.removeItem(this.LEGACY_CAMERA_TOKEN_KEY);
  }

  getCurrentUser(): User | null {
    const userJson = this.getItem(this.USER_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson) as User;
    } catch {
      this.removeItem(this.USER_KEY);
      return null;
    }
  }

  updateCurrentUser(partialUser: Partial<User>): User | null {
    const currentUser = this.getCurrentUser();
    if (!currentUser) return null;

    const updatedUser = {
      ...currentUser,
      ...partialUser,
    };

    this.setItem(this.USER_KEY, JSON.stringify(updatedUser));
    return updatedUser;
  }

  getCurrentAccount(): Account | null {
    const accountJson = this.getItem(this.ACCOUNT_KEY);
    return accountJson ? JSON.parse(accountJson) : null;
  }

  getAccountSelected(): Account | null {
    const accountSelectedJson = this.getItem(this.ACCOUNT_SELECTED_KEY);
    return accountSelectedJson ? JSON.parse(accountSelectedJson) : null;
  }

  setAccountSelected(account: Account): void {
    this.setItem(this.ACCOUNT_SELECTED_KEY, JSON.stringify(account));
  }

  clearAccountSelected(): void {
    this.removeItem(this.ACCOUNT_SELECTED_KEY);
  }

  saveAvailableModules(moduleIds: number[]): void {
    this.setItem(this.MODULES_KEY, JSON.stringify(moduleIds));
  }

  getAvailableModules(): number[] {
    const modulesJson = this.getItem(this.MODULES_KEY);
    return modulesJson ? JSON.parse(modulesJson) : [];
  }

  getCurrentModule(): number | null {
    const moduleJson = this.getItem(this.CURRENT_MODULE_KEY);
    return moduleJson ? JSON.parse(moduleJson) : null;
  }

  setCurrentModule(moduleId: number): void {
    this.setItem(this.CURRENT_MODULE_KEY, JSON.stringify(moduleId));
  }

  clearCurrentModule(): void {
    this.removeItem(this.CURRENT_MODULE_KEY);
  }

  saveCameraIot(cameraId: number, token: string): void {
    this.setItem(this.CAMERA_ID, cameraId.toString());
    this.cameraToken = token.trim() || null;
  }

  getCameraToken(): string | null {
    return this.getProxyCameraToken();
  }

  /**
   * Returns the token accepted by the proxy for the current camera session.
   * Multi-camera authentication is the flow used by the embedded registration,
   * while cameraToken is kept as a fallback for the legacy single-camera flow.
   */
  getProxyCameraToken(): string | null {
    return this.multiCameraToken ?? this.cameraToken;
  }

  getCameraId(): number | null {
    const id = this.getItem(this.CAMERA_ID);
    return id ? Number(id) : null;
  }

  removeCameraIot(): void {
    this.cameraToken = null;
    this.removeItem(this.LEGACY_CAMERA_TOKEN_KEY);
    this.removeItem(this.CAMERA_ID);
  }



  saveMultiCameraIot(cameraIds: number[], token: string): void {
    this.setItem(this.CAMERA_IDS, JSON.stringify(cameraIds));
    this.multiCameraToken = token.trim() || null;
  }

  getMultiCameraToken(): string | null {
    return this.multiCameraToken;
  }

  getMultiCameraIds(): number[] | null {
    const raw = this.getItem(this.CAMERA_IDS);
    return raw ? JSON.parse(raw) as number[] : null;
  }

  getMultiCameraId(): number | null {
    const id = this.getItem(this.CAMERA_ID);
    return id ? Number(id) : null;
  }

  addCameraId(cameraId: number): void {
    const ids = this.getMultiCameraIds() ?? [];
    if (!ids.includes(cameraId)) {
      ids.push(cameraId);
      this.setItem(this.CAMERA_IDS, JSON.stringify(ids));
    }
  }

  removeCameraId(cameraId: number): void {
    const ids = this.getMultiCameraIds() ?? [];
    const filtered = ids.filter(id => id !== cameraId);
    this.setItem(this.CAMERA_IDS, JSON.stringify(filtered));
  }

  
  removeCameraAndTokenIot(): void {
    this.multiCameraToken = null;
    this.removeItem(this.LEGACY_CAMERA_TOKEN_KEY);
    this.removeItem(this.CAMERA_IDS);
  }
}
